import { Injectable, inject, signal } from '@angular/core';
import { ChatMessage } from '../../../core/models/chat-message';
import { RagSearchResult } from '../../rag/models/rag.models';
import { RagService } from '../../rag/services/rag.service';
import { LmStudioReply, LmStudioService } from '../../../core/services/lm-studio.service';
import { ChatPersistenceService } from '../../../core/services/chat-persistence.service';
import { QueryRoute, QueryRouterService } from './query-router.service';
import { WebSearchResult, WebSearchService } from './web-search.service';
import { ThinkingMode } from '../../../core/models/thinking-mode';

export type AgentContext = {
  route: QueryRoute;
  content: string;
  ragResults: RagSearchResult[];
  webResults: WebSearchResult[];
};

export type AgentChatOptions = {
  thinkingMode?: ThinkingMode;
  /** Model used for the visible assistant reply. */
  model?: string;
};
export type InteractiveToolDecision = { tool: 'open_bill_splitter' | 'open_bill_viewer' | null; people: string[]; instructions: string };

@Injectable({ providedIn: 'root' })
export class AgentChatService {
  private readonly router = inject(QueryRouterService);
  private readonly rag = inject(RagService);
  private readonly web = inject(WebSearchService);
  private readonly llm = inject(LmStudioService);
  private readonly persistence = inject(ChatPersistenceService);
  private lastContext: AgentContext = { route: 'local', content: '', ragResults: [], webResults: [] };
  /** Live retrieval state for the chat's progress bubble. */
  readonly status = signal('');

  async chat(messages: ChatMessage[], options: AgentChatOptions = {}): Promise<LmStudioReply> {
    return this.respond(messages, options);
  }

  async decideInteractiveTool(messages: ChatMessage[]): Promise<InteractiveToolDecision> {
    const latest = messages.at(-1);
    if (!latest || latest.author !== 'user') return { tool: null, people: [], instructions: '' };
    // Keep ordinary chat on the normal path. The model classifier is only
    // useful when the request has at least one signal that it concerns a bill.
    if (!this.mayNeedBillTool(latest)) return { tool: null, people: [], instructions: '' };
    const attachmentNote = latest.attachments?.length ? `Attachments: ${latest.attachments.map((a) => `${a.name} (${a.mime})`).join(', ')}` : 'No attachments.';
    const prompt = `Decide whether to invoke a bill UI. Use open_bill_viewer when the user asks to list, view, review, or reopen their saved/past bills and did not provide a new receipt. Use open_bill_splitter for receipts/documents or multi-item shared expenses with multiple people, unequal allocations, shared items, or likely corrections. Do not use either for simple arithmetic such as equally splitting one amount. Return JSON only: {"tool":"open_bill_splitter"|"open_bill_viewer"|null,"people":string[],"instructions":string}. Preserve useful allocation instructions.\n\n${attachmentNote}\nUser: ${latest.text}`;
    try {
      const raw = await this.llm.chat([{ author: 'user', text: prompt, time: latest.time }], { model: this.llm.helperModelId(), maxTokens: 300, timeoutMs: 8_000, useHelperApi: true, systemPrompt: 'You select interactive UI tools. Follow the decision policy and return strict JSON only.' });
      const parsed = JSON.parse(raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '')) as InteractiveToolDecision;
      return { tool: parsed.tool === 'open_bill_splitter' || parsed.tool === 'open_bill_viewer' ? parsed.tool : null, people: Array.isArray(parsed.people) ? parsed.people.filter((p) => typeof p === 'string') : [], instructions: typeof parsed.instructions === 'string' ? parsed.instructions : latest.text };
    } catch { return { tool: null, people: [], instructions: '' }; }
  }

  private mayNeedBillTool(message: ChatMessage): boolean {
    if (message.attachments?.length) return true;
    return /\b(?:bills?|check|receipt|expense|split|share|owe|owed|paid|tip|tax|subtotal|total|history|past)\b/i.test(message.text);
  }

  async stream(
    messages: ChatMessage[],
    options: AgentChatOptions,
    onUpdate: (reply: LmStudioReply) => void,
    signal?: AbortSignal,
  ): Promise<LmStudioReply> {
    return this.respond(messages, options, onUpdate, signal);
  }

  private async respond(messages: ChatMessage[], options: AgentChatOptions, onUpdate?: (reply: LmStudioReply) => void, signal?: AbortSignal): Promise<LmStudioReply> {
    const { thinkingMode, model } = options;
    const latestUser = [...messages].reverse().find((message) => message.author === 'user');
    const query = latestUser?.text;
    if (!query) return this.reply(messages, thinkingMode, model, onUpdate, signal);

    // Attached files are the primary input — answer directly from their content
    // rather than routing to web or RAG retrieval.
    const hasImage = latestUser.attachments?.some((attachment) => attachment.dataUrl);
    const hasFile = latestUser.attachments?.some((attachment) => !attachment.dataUrl && attachment.text);
    if (hasImage || hasFile) {
      this.status.set(hasImage ? 'Analyzing the attached image…' : 'Reading the attached file…');
      return this.reply(messages, thinkingMode, model, onUpdate, signal);
    }

    this.status.set('Deciding…');
    try {
      const decision = await this.router.decide(messages);
      this.logDecision(decision.route, decision.reason, query, undefined, decision.model);
      this.lastContext = { route: decision.route, content: '', ragResults: [], webResults: [] };
      if (decision.route === 'local') {
        this.status.set('Thinking…');
        return await this.reply(messages, thinkingMode, model, onUpdate, signal);
      }

      const retrievalQuery = decision.retrievalQuery || query;
      if (decision.route === 'rag') {
        return await this.answerFromRagOrWeb(messages, query, retrievalQuery, thinkingMode, model, onUpdate, signal);
      }

      return await this.answerFromWebOrLocal(messages, query, retrievalQuery, thinkingMode, model, onUpdate, signal);
    } finally {
      this.status.set('');
    }
  }

  getLastContext(): AgentContext {
    return this.lastContext;
  }

  private async answerFromRagOrWeb(messages: ChatMessage[], query: string, retrievalQuery: string, thinkingMode?: ThinkingMode, model?: string, onUpdate?: (reply: LmStudioReply) => void, signal?: AbortSignal): Promise<LmStudioReply> {
    this.status.set(`Searching your documents for “${this.searchLabel(query)}”…`);
    const results = await this.rag.search(retrievalQuery, 5);
    if (this.hasGoodRagMatch(retrievalQuery, results)) {
      const content = this.rag.buildContext(results);
      this.lastContext = { route: 'rag', content, ragResults: results, webResults: [] };
      this.logDecision('rag', `Using ${results.length} relevant document chunk${results.length === 1 ? '' : 's'}.`, query, results, this.llm.mainModelId());
      this.status.set('Using matching document context to prepare an answer…');
      return this.answerWithContext(messages, content, 'private documents', thinkingMode, model, onUpdate, signal);
    }

    const topScore = results[0]?.score;
    this.logDecision('web', 'No sufficiently relevant document match; searching online instead.', query, { topScore, results }, this.llm.mainModelId());
    this.status.set(`No strong document match — searching online for “${this.searchLabel(query)}”…`);
    return this.answerFromWebOrLocal(messages, query, retrievalQuery, thinkingMode, model, onUpdate, signal);
  }

  private async answerFromWebOrLocal(messages: ChatMessage[], query: string, retrievalQuery: string, thinkingMode?: ThinkingMode, model?: string, onUpdate?: (reply: LmStudioReply) => void, signal?: AbortSignal): Promise<LmStudioReply> {
    this.status.set(`Searching online for “${this.searchLabel(query)}”…`);
    try {
      const results = await this.web.search(retrievalQuery, 5);
      if (!results.length) {
        this.logDecision('local', 'Web search returned no usable sources; falling back to local LLM.', query, undefined, this.llm.mainModelId());
        this.status.set('No web sources found — answering from local knowledge…');
        return this.reply(messages, thinkingMode, model, onUpdate, signal);
      }
      const content = this.web.buildContext(results);
      this.lastContext = { route: 'web', content, ragResults: [], webResults: results };
      this.logDecision('web', `Found ${results.length} web source${results.length === 1 ? '' : 's'}.`, query, results, this.llm.mainModelId());
      this.status.set('Using web sources to prepare an answer…');
      return this.answerWithContext(messages, content, 'web search results', thinkingMode, model, onUpdate, signal);
    } catch (error) {
      // A search outage should not prevent a useful local-only answer.
      this.logDecision('local', 'Web search failed; falling back to local LLM.', query, error, this.llm.mainModelId());
      this.status.set('Web search was unavailable — answering from local knowledge…');
      return this.reply(messages, thinkingMode, model, onUpdate, signal);
    }
  }

  private answerWithContext(messages: ChatMessage[], context: string, source: string, thinkingMode?: ThinkingMode, model?: string, onUpdate?: (reply: LmStudioReply) => void, signal?: AbortSignal): Promise<LmStudioReply> {
    const latestUserIndex = messages.map((message) => message.author).lastIndexOf('user');
    const augmented = messages.map((message, index) => index === latestUserIndex
      ? {
          ...message,
          text: `Answer the user's question using the supplied ${source}. Cite source names or URLs when useful. If the context is insufficient, say so rather than inventing details.\n\nCONTEXT:\n${context}\n\nQUESTION:\n${message.text}`,
        }
      : message);
    return this.reply(augmented, thinkingMode, model, onUpdate, signal);
  }

  private reply(messages: ChatMessage[], thinkingMode?: ThinkingMode, model?: string, onUpdate?: (reply: LmStudioReply) => void, signal?: AbortSignal): Promise<LmStudioReply> {
    const options = { ...this.reasoningOptions(thinkingMode), model };
    return onUpdate
      ? this.llm.streamWithReasoning(messages, options, onUpdate, signal)
      : this.llm.chatWithReasoning(messages, options, signal);
  }

  private reasoningOptions(thinkingMode?: ThinkingMode): { reasoningEffort?: 'low' | 'high' } {
    if (thinkingMode === 'thinking') return { reasoningEffort: 'high' };
    if (thinkingMode === 'instant') return { reasoningEffort: 'low' };
    return {};
  }

  private logDecision(route: QueryRoute, reason: string, query: string, detail?: unknown, model?: string): void {
    if (!this.persistence.getEffectiveSettings().logLlmRequests) return;
    console.info('[Serg agent decision]', { route, reason, query, model, detail });
  }

  /** BM25 has no universal score scale, so require both a reasonable score and query-term coverage. */
  private hasGoodRagMatch(query: string, results: RagSearchResult[]): boolean {
    const best = results[0];
    if (!best || best.score < 0.5) return false;

    const queryTerms = this.meaningfulTerms(query);
    if (!queryTerms.length) return true;
    const chunkTerms = new Set(this.meaningfulTerms(best.chunk.content));
    const coverage = queryTerms.filter((term) => chunkTerms.has(term)).length / queryTerms.length;
    return coverage >= 0.5;
  }

  private meaningfulTerms(text: string): string[] {
    const ignored = new Set(['about', 'according', 'also', 'an', 'and', 'are', 'can', 'could', 'did', 'do', 'does', 'file', 'files', 'for', 'from', 'how', 'in', 'is', 'it', 'my', 'notes', 'of', 'on', 'our', 'please', 'tell', 'that', 'the', 'this', 'to', 'uploaded', 'was', 'what', 'who', 'with', 'would', 'you']);
    return [...new Set(text.toLowerCase().match(/[\p{L}\p{N}]{3,}/gu) ?? [])].filter((term) => !ignored.has(term));
  }

  private searchLabel(query: string): string {
    return query.trim().replace(/\s+/g, ' ').slice(0, 100);
  }
}
